// Services
import { DataTypeEvaluator, toEmail } from '@ply-io/data-types';

import { FieldType, AI_DEFAULT_MAX_WORDS } from './widgetFieldTypes.const';
import { FeatureTypes } from '@/components/Billing/billing-types';
import AppService from '@/utils/AppService';
import ParamsService from '@/utils/ParamsService';
import CredentialsService from '@/utils/credentials-service';
import DynamicDataService from '@/utils/dynamic-data-service';
//
const evaluator = new DataTypeEvaluator();

// Field Components
import AIInputField from './WidgetFields/AIInputField';
import LoomInputField from './WidgetFields/LoomInputField';
import ImageEditorInputField from './WidgetFields/ImageEditorInputField';
import IframeField from './WidgetFields/IframeField';
import DividerField from './WidgetFields/DividerField';
import GalleryField from './WidgetFields/GalleryField';
import FileInputField from './WidgetFields/FileInputField';
import TextInputField from './WidgetFields/TextInputField';
import TextOutputField from './WidgetFields/TextOutputField';
import EmailInputField from './WidgetFields/EmailInputField';
import DatePickerField from './WidgetFields/DatePickerField';
import DateTimePickerField from './WidgetFields/DateTimePickerField';
import CardsField from './WidgetFields/CardsField/CardsField';
import TableField from './WidgetFields/TableField/TableField';
import RecordAudioField from './WidgetFields/RecordAudioField';
import NumberInputField from './WidgetFields/NumberInputField';
import SearchField from './WidgetFields/SearchField/SearchField';
import RichTextInputField from './WidgetFields/RichTextInputField';
import DropdownField from './WidgetFields/DropdownField/DropdownField';
import CredentialsSelectorField from './WidgetFields/CredentialsSelectorField';
import UserSchemaMappingsSelectorField from './WidgetFields/UserSchemaMappingsSelectorField';
import TableMapperInputField from './WidgetFields/TableMapperInputField/TableMapperInputField';
import SwitchField from './WidgetFields/SwitchField';
import CascadingDropdownField from './WidgetFields/CascadingDropdownField/CascadingDropdownField';
import ImageDisplayField from './WidgetFields/ImageDisplayField/ImageDisplayField';
import VideoPlayerField from './WidgetFields/VideoPlayerField/VideoPlayerField';
import EndRunButtonsField from './WidgetFields/EndRunButtonsField/EndRunButtonsField';
import HubSpotSchedulerInputField from './WidgetFields/HubspotSchedulerInputField.vue';
import CalendlyInputField from './WidgetFields/CalendlyInputField';
import StripeInputField from './WidgetFields/StripeInputField';

// Field Settings Components
import AIInputFieldSettings from './FieldSettings/AIInputFieldSettings';
import LoomFieldSettings from './FieldSettings/LoomFieldSettings';
import CalendlyFieldSettings from './FieldSettings/CalendlyFieldSettings';
import ImageEditorFieldSettings from './FieldSettings/ImageEditorFieldSettings';
import IframeFieldSettings from './FieldSettings/IframeFieldSettings';
import DividerFieldSettings from './FieldSettings/DividerFieldSettings';
import GalleryFieldSettings from './FieldSettings/GalleryFieldSettings';
import TextInputFieldSettings from './FieldSettings/TextInputFieldSettings';
import RichTextInputFieldSettings from './FieldSettings/RichTextInputFieldSettings';
import FileInputFieldSettings from './FieldSettings/FileInputFieldSettings';
import EmailInputFieldSettings from './FieldSettings/EmailInputFieldSettings';
import DatePickerFieldSettings from './FieldSettings/DatePickerFieldSettings';
import DateTimePickerFieldSettings from './FieldSettings/DateTimePickerFieldSettings';
import AudioInputFieldSettings from './FieldSettings/AudioInputFieldSettings';
import TextOutputFieldSettings from './FieldSettings/TextOutputFieldSettings';
import NumberInputFieldSettings from './FieldSettings/NumberInputFieldSettings';
import CardsFieldSettings from './FieldSettings/CardsFieldSettings/CardsFieldSettings';
import TableFieldSettings from './FieldSettings/TableFieldSettings/TableFieldSettings';
import WidgetFieldSearch from './FieldSettings/SearchFieldSettings/SearchFieldSettings';
import DropdownFieldSettings from './FieldSettings/DropdownFieldSettings/DropdownFieldSettings';
import CredentialsSelectorFieldSettings from './FieldSettings/CredentialsSelectorFieldSettings';
import TableMapperFieldSettings from './FieldSettings/TableMapperFieldSettings/TableMapperFieldSettings';
import UserSchemaMappingsSelectorFieldSettings from './FieldSettings/UserSchemaMappingsSelectorFieldSettings';
import SwitchFieldSettings from './FieldSettings/Switch/SwitchFieldSettings';
import CascadingDropdownFieldSettings from './FieldSettings/CascadingDropdownFieldSettings/CascadingDropdownFieldSettings';
import ImageDisplayFieldSettings from './FieldSettings/ImageDisplayFieldSettings/ImageDisplayFieldSettings';
import VideoPlayerFieldSettings from './FieldSettings/VideoPlayerFieldSettings/VideoPlayerFieldSettings';
import EndRunButtonsFieldSettings from './FieldSettings/EndRunButtonsFieldSettings/EndRunButtonsFieldSettings';
import FooterFieldSettings from './FieldSettings/FooterFieldSettings/FooterFieldSettings';
import StripeFieldSettings from './FieldSettings/StripeFieldSettings';
import HubSpotSchedulerFieldSettings from './FieldSettings/HubspotSchedulerFieldSettings.vue';

// MiniApps "edit display" components
import WidgetEditorTableMapper from './WidgetFields/TableMapperInputField/WidgetEditorTableMapper';

import { CredentialsTypes } from '@ply-io/ply-front-shared-utilities';

import TableActionEditorService from '../BlockEditor/TableActionEditor/table-action-edtior-service';
import { isNullOrUndefined } from '@/utils/helpers';
import GoogleDocsFieldSettings from './FieldSettings/GoogleDocsFieldSettings.vue';
import GoogleDocsInputField from './WidgetFields/GoogleDocsInputField.vue';
import { CONFIRMATION_REDIRECT_OPTIONS } from './confirmation-mini-app.const';

const FIELD_CONFIGURATIONS_ENRICHED = [
	{
		id: -66,
		defaultValue: null,
		type: FieldType.CredentialsSelector,
		name: 'Connection',
		label: 'Connection',
		draw: 'draw-connection',
		skeletonType: 'paragraph',
		settingsTitle: 'Connection',
		icon: 'widget-field-connection',
		description: 'Choose credentials for service',
		defaultMetadata: {
			source: { autocomplete: true },
			options: {
				additional_title: { enabled: false, value: '' },
				required: { enabled: true },
			},
		},
		validator: (fieldValue) => !!fieldValue,
		// Components:
		component: CredentialsSelectorFieldSettings,
		widgetFieldComponent: CredentialsSelectorField,
	},
	{
		id: -66,
		defaultValue: null,
		type: FieldType.UserSchemaMappingsSelector,
		name: 'Data Mapper',
		label: 'Data Mapper',
		draw: 'draw-mappings',
		skeletonType: 'paragraph',
		settingsTitle: 'Mappings Block',
		icon: 'widget-field-mappings',
		description:
			'Used when installing features for custom database apps. Lets users link their custom properties to the ones used when you built the feature.',
		defaultMetadata: {
			source: {
				field_item: '',
				field_title: '',
				credentials_configuration: {
					source_field_uuid: null,
					credentials_type: null,
				},
				params_map: {},
				params_map_labels: {},
				mappings: [], //list of objects like: [{ originalKey: <old_value> displayName: <name> }]
				service: null,
				service_version: null,
			},
			options: {
				additional_title: { enabled: false, value: '' },
				identifier: { value: 'Mapping Block' },
				required: { enabled: true },
			},
		},
		validator: (fieldValue) =>
			!!fieldValue?.source?.credentials_configuration?.source_field_uuid &&
			!!fieldValue?.source?.credentials_configuration.credentials_type,
		getDefaultIdentifierForWidgetField: (widgetField, extraParams) => {
			const mapperAndMapperName = `${widgetField.metadata.options?.identifier?.value || 'Mapping Block'} - ${
				widgetField.metadata.source?.field_title
			}`;
			if (extraParams?.keySuffixParts.length >= 2) {
				const mappingParams = ParamsService.getParamsForMappingByItem(
					extraParams?.getService(widgetField.metadata.source?.service) || {},
					widgetField.metadata.source?.field_item
				);
				const paramsMapValue = extraParams.keySuffixParts.shift();
				if (paramsMapValue === ParamsService.MAPPER_REQUIRED_PARAMS && mappingParams) {
					const keyLabels = extraParams.keySuffixParts.map((paramKey) => mappingParams.find((x) => x.key === paramKey)?.name || paramKey);
					const paramKey = keyLabels.join(' > ');
					return `${mapperAndMapperName} - ${paramKey}`;
				}
			}
			return mapperAndMapperName;
		},
		// Components:
		component: UserSchemaMappingsSelectorFieldSettings,
		widgetFieldComponent: UserSchemaMappingsSelectorField,
	},
	{
		id: -1,
		type: FieldType.TableMapper,
		name: 'TABLE_MAPPER',
		draw: 'draw-text-input',
		label: 'Table Mapper label',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		settingsTitle: 'Table mapper',
		description: 'TABLE_MAPPER',
		properlyFilledValidator: (widgetField) => !!widgetField.metadata.options?.step_id?.value,
		defaultMetadata: {
			options: {
				step_id: { value: null },
				sort: { enabled: false },
				find: { enabled: false },
				editable: { enabled: false },
				columns_options: [{ id: 1, text: '', value: '' }],
				user_selection: { enabled: false, multiple: false },
				dropdown_mode: { value: 0 },
				previous_steps: { table_mode_source: { value: '' } },
				table_mode: { text_column: '' },
				headers_mapping_mode: { enabled: false },
			},
		},
		// Components:
		component: TableMapperFieldSettings,
		widgetFieldComponent: TableMapperInputField,
		widgetEditorComponent: WidgetEditorTableMapper,
		hidden: true,
	},
	{
		id: -4,
		defaultValue: '',
		name: 'Text Output',
		type: FieldType.ParagraphOutput,
		draw: 'draw-paragraph',
		icon: 'widget-field-text-output',
		skeletonType: 'paragraph',
		label: 'Text Output label',
		settingsTitle: 'Text Output',
		description: 'Explain something or present content from your flow.',
		defaultMetadata: {
			output: {
				value: 'Hello there 👋',
			},
			options: {
				title: { value: 'Text label', enabled: false },
			},
		},
		// Components:
		component: TextOutputFieldSettings,
		widgetFieldComponent: TextOutputField,
	},
	{
		id: -2,
		defaultValue: '',
		type: FieldType.TextInput,
		name: 'Text Input',
		draw: 'draw-text-input',
		label: 'Text Input label',
		settingsTitle: 'Text Input',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		description: 'For one line of text like names or short descriptions.',
		defaultMetadata: {
			options: {
				longText: { enabled: false },
			},
		},
		validator: (fieldValue = '') => String(fieldValue).length > 0,
		// Components:
		component: TextInputFieldSettings,
		widgetFieldComponent: TextInputField,
	},
	{
		id: -93,
		defaultValue: '',
		type: FieldType.RichTextInput,
		name: 'Rich Text Input',
		draw: 'draw-text-input',
		label: 'Rich Text Input label',
		settingsTitle: 'Rich Text Input',
		skeletonType: 'heading, text',
		icon: 'widget-field-rich-text-input',
		description: 'For text enriched with features such as Bold, Underline, Bullets etc..',
		defaultMetadata: {
			options: {
				longText: { enabled: false },
			},
		},
		validator: (fieldValue = '') => {
			const tempDivElement = document.createElement('div');
			tempDivElement.innerHTML = fieldValue;
			const text = tempDivElement.textContent || tempDivElement.innerText;
			return text.length > 0;
		},
		// Components:
		component: RichTextInputFieldSettings,
		widgetFieldComponent: RichTextInputField,
	},
	{
		id: -9,
		defaultValue: '',
		type: FieldType.NumberInput,
		name: 'Number Input',
		draw: 'draw-number-input',
		label: 'Number Input label',
		skeletonType: 'heading, text',
		settingsTitle: 'Number Input',
		icon: 'widget-field-number-input',
		description: 'Good for quantities, currencies, and the meaning of life.',
		defaultMetadata: {
			output: { thousands_seperator: ',', decimal_seperator: '.', prefix: '', suffix: '' },
			validations: {
				decimal: { key: 'decimal', value: 2, enabled: true },
				min_number: { key: 'min_number', value: null, enabled: false },
				max_number: { key: 'max_number', value: null, enabled: false },
			},
		},
		validator: (fieldValue = '', field) => {
			const fieldValidationMethods = field.metadata.validations;
			const shouldValidateMinValue = fieldValidationMethods.min_number.enabled;
			const shouldValidateMaxValue = fieldValidationMethods.max_number.enabled;
			const shouldValidateDecimalPoint = fieldValidationMethods.decimal.enabled;
			if (String(fieldValue).length === 0) {
				return false;
			}
			if (shouldValidateMinValue) {
				if (fieldValidationMethods.min_number.value >= fieldValue) {
					return false;
				}
			}
			if (shouldValidateMaxValue) {
				if (fieldValidationMethods.max_number.value <= fieldValue) {
					return false;
				}
			}
			if (shouldValidateDecimalPoint) {
				const decimalSeparator = '.';
				const numberOfDigitsAfterDecimalPointAllowed = fieldValidationMethods.decimal.value;
				const numberOfDigitsAfterDecimalPoint = String(fieldValue).split(decimalSeparator)[1]?.length || 0;
				if (numberOfDigitsAfterDecimalPoint > numberOfDigitsAfterDecimalPointAllowed) {
					return false;
				}
			}
			return true;
		},
		// Components:
		component: NumberInputFieldSettings,
		widgetFieldComponent: NumberInputField,
	},
	{
		id: -12,
		defaultValue: '',
		type: FieldType.EmailInput,
		name: 'Email Input',
		draw: 'draw-email-input',
		label: 'Email Input label',
		skeletonType: 'heading, text',
		settingsTitle: 'Email Input',
		icon: 'widget-field-email-input',
		description: 'Good for emails.',
		defaultMetadata: {
			validations: {
				email: { key: 'email', enabled: true },
				format: { key: 'format', value: '', enabled: false },
				host: { key: 'host', placeholder: 'ply.io', enabled: false },
			},
		},
		validator: (fieldValue = '', field) => {
			const shouldValidateEmail = field.metadata.validations.email.enabled;
			const shouldValidateHost = field.metadata.validations.host.enabled;
			const shouldValidateFormat = field.metadata.validations.format.enabled;
			if (String(fieldValue).length === 0) {
				return false;
			}
			if (shouldValidateEmail) {
				if (!evaluator.isEmail(fieldValue)) {
					return false;
				}
			}
			if (shouldValidateHost) {
				const email = toEmail(fieldValue);
				if (email && email.domain?.toLowerCase() !== field.metadata.validations.host.value?.toLowerCase()) {
					return false;
				}
			}
			if (shouldValidateFormat) {
				const re = new RegExp(field.metadata.validations.format.value);
				if (!re.test(fieldValue)) {
					return false;
				}
			}
			return true;
		},
		// Components:
		component: EmailInputFieldSettings,
		widgetFieldComponent: EmailInputField,
	},
	{
		id: -1,
		type: FieldType.Search,
		name: 'Search',
		defaultValue: '',
		draw: 'draw-search',
		label: 'Search label',
		complexDefaultValue: [],
		settingsTitle: 'Search',
		icon: 'widget-field-search',
		skeletonType: 'heading, text',
		description: 'Search and choose an item directly in any of your apps.',
		properlyFilledValidator: (widgetField, store) => {
			if (!widgetField.metadata.source) {
				return false;
			}
			const {
				service: serviceId,
				action: actionId,
				credentials: credentialsId,
				credentials_configuration,
				user_schema_mappings_source_field_uuid,
			} = widgetField.metadata.source;
			const service = store.getters['apps/getService'](serviceId);
			const action = store.getters['actions/getAction'](actionId);
			if (!service || !action) {
				return false;
			}

			if (!AppService.doesRequiresAuth(service)) {
				return true;
			}

			if (credentials_configuration && CredentialsService.isConnectionFromConfiguration(credentials_configuration.credentials_type)) {
				const sourceFieldUuid = credentials_configuration.source_field_uuid;
				if (!sourceFieldUuid) {
					return false;
				}
				const conf = store.getters['selectedWorkflow/configurations/getConfigurationAndInterfaceIdByFieldUUID'](sourceFieldUuid);
				const fieldResponse = store.getters['workflowConfigurations/getConfigurationFieldResponse'](conf.configurationId, sourceFieldUuid);
				if (!fieldResponse) {
					return false;
				}

				if (action.has_user_schema) {
					if (!user_schema_mappings_source_field_uuid) {
						return false;
					}
					const conf = store.getters['selectedWorkflow/configurations/getConfigurationAndInterfaceIdByFieldUUID'](
						user_schema_mappings_source_field_uuid
					);
					const fieldResponse = store.getters['workflowConfigurations/getConfigurationFieldResponse'](
						conf.configurationId,
						user_schema_mappings_source_field_uuid
					);
					if (!DynamicDataService.isMappingResponseValid(fieldResponse)) {
						return false;
					}
				}
				return true;
			} else {
				return !!credentialsId; // casting to boolean
			}
		},
		defaultMetadata: {
			source: {
				autocomplete: true,
				service: null,
				action: null,
				params_map: {},
				params_map_labels: {},
				params_def: [],
				credentials: null,
				credentials_configuration: {
					source_field_uuid: null,
					credentials_type: CredentialsTypes.Embedded,
				},
			},
			options: {
				noun: '',
				identifier: { value: 'Search' },
				user_selection: { multiple: false },
				custom: {
					enabled: false,
					sample: { value: undefined },
					description: { enabled: false, value: undefined },
					image: { enabled: false, value: undefined },
				},
				searchPrefill: {
					enabled: false,
					value: '',
					preselect: { enabled: false },
				},
			},
		},
		validator: (fieldValue) => !!fieldValue,
		// Components:
		component: WidgetFieldSearch,
		widgetFieldComponent: SearchField,
	},
	{
		id: -99,
		type: FieldType.Dropdown,
		defaultValue: '',
		name: 'Dropdown',
		draw: 'draw-dropdown',
		label: 'Dropdown label',
		complexDefaultValue: [],
		settingsTitle: 'Dropdown',
		icon: 'widget-field-dropdown',
		skeletonType: 'heading, text',
		description: 'Choose an option from a list. Optionally, with auto-complete.',
		defaultMetadata: {
			options: {
				preselect: { enabled: false },
				option_value: { enabled: false },
				autocomplete: { enabled: false },
				dropdown_mode: { enabled: false, value: 0 },
				user_selection: { multiple: false },
				user_custom_options: { enabled: false },
				dropdown_options: [{ id: 1, text: '', value: '' }],
				table_mode: { text_column: null, value_column: null },

				previous_steps: { table_mode_source: { value: null } },
			},
		},
		validator: (fieldValue) => {
			if (fieldValue === undefined || fieldValue === null) {
				return false;
			} else if (Array.isArray(fieldValue)) {
				return fieldValue.length > 0;
			} else if (fieldValue === '') {
				return false;
			}
			return true;
		},
		// Components:
		component: DropdownFieldSettings,
		widgetFieldComponent: DropdownField,
	},
	{
		id: -101,
		type: FieldType.Gallery,
		defaultValue: '',
		draw: 'draw-gallery',
		name: 'Image Gallery',
		label: 'Image Gallery label',
		icon: 'widget-field-gallery',
		skeletonType: 'heading, text',
		settingsTitle: 'Image Gallery',
		description: 'Show multiple images. Optionally, let the user choose one or more of them.',
		properlyFilledValidator: (widgetField) => !!widgetField.metadata.options.step_id?.value,
		defaultMetadata: {
			options: {
				step_id: { value: null },
				user_selection: { enabled: false },
			},
		},
		// Components:
		component: GalleryFieldSettings,
		widgetFieldComponent: GalleryField,
	},
	{
		id: -8,
		defaultValue: '',
		type: FieldType.AudioInput,
		icon: 'audio-input',
		name: 'Audio Recorder',
		draw: 'draw-record-audio',
		settingsTitle: 'Audio Recorder',
		label: 'Audio Recorder label',
		skeletonType: 'heading, text',
		description: 'For when saying something is just better than writing it.',
		validator: (fieldValue) => fieldValue?.length > 0,
		// Components:
		component: AudioInputFieldSettings,
		widgetFieldComponent: RecordAudioField,
	},
	{
		id: -63,
		defaultValue: '',
		type: FieldType.DateTimePicker,
		icon: 'datepicker',
		label: 'Date Time label',
		name: 'Date Time picker',
		settingsTitle: 'Date Time',
		draw: 'draw-datepicker',
		skeletonType: 'heading, text',
		description: 'Let the user choose a date and time.',
		defaultMetadata: {
			options: {
				prefill: { enabled: false, value: { date: '', time: '' } },
				timezone: {
					option_selected: { value: false },
					custom: { value: 'UTC' },
					show: { enabled: false },
				},
				time: {
					all_hours_format: { enabled: false },
				},
			},
			validations: {
				min_date: { key: 'min_date', value: null, enabled: false, is_mapped: false },
				max_date: { key: 'max_date', value: null, enabled: false, is_mapped: false },
				min_time: { key: 'min_time', value: null, enabled: false, is_mapped: false },
				max_time: { key: 'max_time', value: null, enabled: false, is_mapped: false },
				time_interval: { key: 'time_interval', value: null, enabled: false },
			},
		},
		validator: (fieldValue = '', field) => {
			const fieldValidationMethods = field.metadata.validations;
			const prefillEnabled = field.metadata?.options.prefill?.enabled;
			const shouldValidateMinValue =
				fieldValidationMethods.min_date.enabled && !fieldValidationMethods.min_date.is_mapped && !prefillEnabled;
			const shouldValidateTimeMinValue =
				fieldValidationMethods.min_time.enabled && !fieldValidationMethods.min_time.is_mapped && !prefillEnabled;
			const shouldValidateMaxValue =
				fieldValidationMethods.max_date.enabled && !fieldValidationMethods.max_date.is_mapped && !prefillEnabled;
			const shouldValidateTimeMaxValue =
				fieldValidationMethods.max_time.enabled && !fieldValidationMethods.max_time.is_mapped && !prefillEnabled;
			if (!fieldValue?.iso || !fieldValue?.date || !fieldValue?.time || !fieldValue?.timezone) {
				return false;
			}
			if (shouldValidateMinValue) {
				if (new Date(fieldValidationMethods.min_date.value) > new Date(fieldValue.date)) {
					return false;
				}
			}
			if (shouldValidateMaxValue) {
				if (new Date(fieldValidationMethods.max_date.value) < new Date(fieldValue.date)) {
					return false;
				}
			}
			if (shouldValidateTimeMinValue) {
				if (new Date('1.1.90 ' + fieldValidationMethods.min_time.value).getTime() > new Date('1.1.90 ' + fieldValue.time).getTime()) {
					return false;
				}
			}
			if (shouldValidateTimeMaxValue) {
				if (new Date('1.1.90 ' + fieldValidationMethods.min_time.value).getTime() < new Date('1.1.90 ' + fieldValue.time).getTime()) {
					return false;
				}
			}
			return true;
		},
		// Components:
		component: DateTimePickerFieldSettings,
		widgetFieldComponent: DateTimePickerField,
	},
	{
		id: -63,
		defaultValue: '',
		type: FieldType.DatePicker,
		icon: 'datepicker',
		label: 'Date label',
		name: 'Date picker',
		settingsTitle: 'Date',
		draw: 'draw-datepicker',
		skeletonType: 'heading, text',
		description: 'Let the user choose a date.',
		defaultMetadata: {
			options: {
				prefill: { enabled: false, value: '' },
			},
			validations: {
				min_date: { key: 'min_date', value: null, enabled: false },
				max_date: { key: 'max_date', value: null, enabled: false },
			},
		},
		validator: (fieldValue = '', field) => {
			const fieldValidationMethods = field.metadata.validations;
			const shouldValidateMinValue = fieldValidationMethods.min_date.enabled;
			const shouldValidateMaxValue = fieldValidationMethods.max_date.enabled;
			if (fieldValue.length === 0) {
				return false;
			}
			if (shouldValidateMinValue) {
				if (new Date(fieldValidationMethods.min_date.value) > new Date(fieldValue)) {
					return false;
				}
			}
			if (shouldValidateMaxValue) {
				if (new Date(fieldValidationMethods.max_date.value) < new Date(fieldValue)) {
					return false;
				}
			}
			return true;
		},
		// Components:
		component: DatePickerFieldSettings,
		widgetFieldComponent: DatePickerField,
	},
	{
		id: -69,
		type: FieldType.Cards,
		name: 'Cards',
		icon: 'cards',
		defaultValue: {},
		draw: 'draw-cards',
		label: 'Cards label',
		complexDefaultValue: [],
		settingsTitle: 'Cards Block',
		skeletonType: 'image, image',
		description: 'Display multi-item data like contacts, deals or orders. Filter, sort, edit or select items.',
		properlyFilledValidator: (widgetField) => {
			const stepIdDefined = !!widgetField.metadata.options.step_id?.value;
			const templateChosen = !!widgetField.metadata.options.template;
			return stepIdDefined && templateChosen;
		},
		defaultMetadata: {
			options: {
				step_id: { value: null },
				sort: { enabled: false },
				find: { enabled: false },
				user_selection: { enabled: true, multiple: false },
				submit_on_select: { enabled: false },
				template: { value: null },
			},
			params_map: {},
		},
		validator: (fieldValue) =>
			fieldValue?.length > 0 || (fieldValue && typeof fieldValue === 'object' && Object.keys(fieldValue).length > 0),
		// Components:
		component: CardsFieldSettings,
		widgetFieldComponent: CardsField,
	},
	{
		id: -77,
		type: FieldType.Table,
		icon: 'table',
		name: 'Table',
		defaultValue: {},
		draw: 'draw-table',
		label: 'Table label',
		complexDefaultValue: [],
		settingsTitle: 'Table Block',
		skeletonType: 'image, image',
		description: 'Display and edit multi-item data like contacts, deals or orders. Filter, sort, edit or select items.',
		properlyFilledValidator: (widgetField) => {
			const stepIdDefined = !!widgetField.metadata.options.step_id?.value;
			const headersDefined = widgetField.metadata.options.headers && widgetField.metadata.options.headers.length > 0;
			return stepIdDefined && headersDefined;
		},
		defaultMetadata: {
			options: {
				step_id: { value: null },
				sort: { enabled: false },
				find: { enabled: false },
				editable: { enabled: false },
				add_rows: { enabled: false },
				user_selection: { enabled: true, multiple: false },
				page_size: { value: TableActionEditorService.DEFAULT_TABLE_PAGE_SIZE },
				column_order: { value: TableActionEditorService.DEFAULT_TABLE_COLUMN_ORDER },
			},
		},
		validator: (fieldValue) =>
			fieldValue?.length > 0 || (fieldValue && typeof fieldValue === 'object' && Object.keys(fieldValue).length > 0),
		// Components:
		component: TableFieldSettings,
		widgetFieldComponent: TableField,
	},
	{
		id: -100,
		type: FieldType.Iframe,
		icon: 'iframe',
		name: 'Iframe',
		defaultValue: '',
		draw: 'draw-iframe',
		label: 'Iframe label',
		settingsTitle: 'Iframe',
		skeletonType: 'image, image, image',
		description: 'Open a URL in an iframe.',
		properlyFilledValidator: (widgetField) => !!widgetField.metadata.options?.previous_steps?.url?.value,
		defaultMetadata: {
			options: {
				previous_steps: {
					query_params: [],
					url: { value: null },
				},
			},
		},
		// Components:
		component: IframeFieldSettings,
		widgetFieldComponent: IframeField,
		billingFeatureId: FeatureTypes.CustomerIframeBlock,
	},
	{
		id: -105,
		defaultValue: '',
		type: FieldType.FileInput,
		icon: 'fileinput',
		name: 'File Upload',
		draw: 'draw-fileinput',
		label: 'File Upload label',
		complexDefaultValue: [],
		description: 'File upload',
		settingsTitle: 'File upload',
		skeletonType: 'image, image, image',
		validator: (fieldValue) => fieldValue?.length > 0,
		defaultMetadata: {
			options: {
				user_selection: {
					multiple: false,
				},
			},
		},
		// Components:
		component: FileInputFieldSettings,
		widgetFieldComponent: FileInputField,
	},
	{
		id: -32,
		defaultValue: '',
		type: FieldType.AIInput,
		name: 'AI Assistant',
		draw: 'draw-text-input',
		label: 'AI Assistant',
		settingsTitle: 'AI Assistant',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		description: 'Add some AI Magic to your apps',
		validator: (fieldValue = '') => String(fieldValue).length > 0,
		defaultMetadata: {
			options: {
				prefill: { enabled: false },
				maxWords: { enabled: false, value: AI_DEFAULT_MAX_WORDS },
				allowFineTuning: { enabled: false, value: true },
				prompt_prefix: { enabled: false, value: '' },
				auto_submit_prefilled: { enabled: false },
				prompt_suffix: { enabled: false, value: '' },
				placeholder: { enabled: false, value: '' },
				required: { enabled: true },
			},
		},
		// Components:
		component: AIInputFieldSettings,
		widgetFieldComponent: AIInputField,
		hidden: true,
	},
	{
		id: -33,
		defaultValue: '',
		type: FieldType.Loom,
		name: 'Loom recorder',
		draw: 'draw-text-input',
		label: 'Loom recorder',
		settingsTitle: 'Loom recorder',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		description: 'Record your screen and yourself on the fly',
		validator: (fieldValue = '') => String(fieldValue).length > 0,
		defaultMetadata: {
			options: {
				required: { enabled: true },
			},
		},
		// Components:
		component: LoomFieldSettings,
		widgetFieldComponent: LoomInputField,
		hidden: true,
	},
	{
		id: -34,
		defaultValue: '',
		type: FieldType.ImageEditor,
		name: 'Image editor',
		draw: 'draw-text-input',
		label: 'Image editor',
		settingsTitle: 'Image editor',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		description: 'Crop, rotate, resize, filter, annotate, adjust colors, and much more...',
		validator: (fieldId, fieldValue) => !!fieldValue.metadata.options.image_src.value,
		defaultMetadata: {
			options: {
				image_src: { value: '' },
				file_format: { value: '' },
			},
		},
		// Components:
		component: ImageEditorFieldSettings,
		widgetFieldComponent: ImageEditorInputField,
		hidden: true,
	},
	{
		id: -35,
		defaultValue: '',
		type: FieldType.Calendly,
		name: 'Calendly embed',
		draw: 'draw-text-input',
		label: 'Calendly embed',
		settingsTitle: 'Calendly embed',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		description: 'Set an appointment with a calendly event',
		validator: (fieldValue = {}) => !isNullOrUndefined(fieldValue.invitee),
		defaultMetadata: {
			options: {
				calendly_link: { value: '' },
				is_add_cookies: { enabled: true },
				is_add_description: { enabled: true },
				confirmation_handling_option: { value: CONFIRMATION_REDIRECT_OPTIONS.withAnimation },
			},
		},
		// Components:
		component: CalendlyFieldSettings,
		widgetFieldComponent: CalendlyInputField,
		hidden: true,
	},
	{
		id: -36,
		defaultValue: '',
		type: FieldType.HubSpotScheduler,
		name: 'HubSpot Scheduler',
		draw: 'draw-text-input',
		label: 'HubSpot Scheduler',
		settingsTitle: 'HubSpot Scheduler',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		description: 'Schedule a meeting with HubSpot scheduler',
		validator: (fieldValue = {}) => !isNullOrUndefined(fieldValue.duration),
		defaultMetadata: {
			options: {
				hubspot_link: { value: '' },
				confirmation_handling_option: { value: CONFIRMATION_REDIRECT_OPTIONS.withAnimation },
			},
		},
		// Components:
		component: HubSpotSchedulerFieldSettings,
		widgetFieldComponent: HubSpotSchedulerInputField,
		hidden: true,
	},
	{
		id: -38,
		defaultValue: '',
		type: FieldType.GoogleDocs,
		name: 'Google Docs Editor',
		draw: 'draw-text-input',
		label: 'Google Docs Editor',
		settingsTitle: 'Google Docs Editor',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		description: 'Edit a Google Doc',
		validator: () => true,
		defaultMetadata: {
			options: {
				docs_id: { value: '' },
			},
		},
		component: GoogleDocsFieldSettings,
		widgetFieldComponent: GoogleDocsInputField,
	},
	{
		id: -37,
		defaultValue: '',
		type: FieldType.Stripe,
		name: 'Stripe Embedded Checkout',
		draw: 'draw-text-input',
		label: 'Stripe Embedded Checkout',
		settingsTitle: 'Stripe Embedded Checkout',
		skeletonType: 'heading, text',
		icon: 'widget-field-text-input',
		description: 'Checkout and pay with Stripe',
		validator: () => true,
		defaultMetadata: {
			options: {
				client_secret: { value: '' },
				stripe_key: { value: '' },
				confirmation_handling_option: { value: CONFIRMATION_REDIRECT_OPTIONS.withAnimation },
			},
		},
		// Components:
		component: StripeFieldSettings,
		widgetFieldComponent: StripeInputField,
		hidden: true,
	},
	{
		id: -106,
		name: 'Divider',
		icon: 'divider',
		label: 'Divider',
		defaultValue: '',
		draw: 'draw-divider',
		settingsTitle: 'Divider',
		description: 'Section Divider',
		type: FieldType.Divider,
		skeletonType: 'image, image, image',
		validator: () => true, // this is a must, to tell the form validator this field is valid
		// Components:
		component: DividerFieldSettings,
		widgetFieldComponent: DividerField,
	},
	{
		id: -107,
		defaultValue: false,
		name: 'Toggle',
		type: FieldType.Switch,
		draw: 'draw-switch',
		icon: 'widget-field-switch',
		skeletonType: 'heading, text',
		label: 'Toggle label',
		settingsTitle: 'Toggle',
		description: 'Allow user to select whether something is True or False',
		defaultMetadata: {
			options: {
				switch_label: 'Yes/No',
			},
		},
		validator: (fieldValue) => [true, 'true'].includes(fieldValue),
		// Components:
		component: SwitchFieldSettings,
		widgetFieldComponent: SwitchField,
	},
	{
		id: -177,
		type: FieldType.CascadingDropdown,
		defaultValue: '',
		name: 'Cascading Dropdown',
		label: 'Cascading Dropdown label',
		complexDefaultValue: [],
		settingsTitle: 'Cascading Dropdown',
		// icon: 'widget-field-dropdown',
		skeletonType: 'heading, text',
		description: 'Choose an option from a list. Optionally, with auto-complete.',
		defaultMetadata: {
			options: {
				preselect: { enabled: false },
				option_value: { enabled: false },
				autocomplete: { enabled: false },
				dropdown_mode: { enabled: false, value: 0 },
				user_selection: { multiple: false },
				user_custom_options: { enabled: false },
				dropdown_options: [{ id: 1, text: '', value: '' }],
				table_mode: { text_column: null, value_column: null },
				previous_steps: { table_mode_source: { value: null } },
			},
		},
		validator: (fieldValue) => fieldValue?.length > 0,
		// Components:
		component: CascadingDropdownFieldSettings,
		widgetFieldComponent: CascadingDropdownField,
	},
	{
		id: -187,
		type: FieldType.ImageDisplay,
		defaultValue: '',
		name: 'Image Display',
		label: 'Image Display label',
		complexDefaultValue: [],
		settingsTitle: 'Image Display',
		skeletonType: 'heading, text',
		description: 'Display an image.',
		defaultMetadata: {
			options: {
				image_display_url: '',
				is_image_url_from_uploaded_file: false,
			},
		},
		// Components:
		component: ImageDisplayFieldSettings,
		widgetFieldComponent: ImageDisplayField,
	},
	{
		id: -189,
		type: FieldType.VideoPlayer,
		defaultValue: '',
		name: 'Video player',
		label: 'Video player label',
		complexDefaultValue: [],
		settingsTitle: 'Video player',
		skeletonType: 'image',
		description: 'Play a selected Video',
		defaultMetadata: {
			options: {
				video_player_src: '',
				streaming_service_type: '',
			},
		},
		// Components:
		component: VideoPlayerFieldSettings,
		widgetFieldComponent: VideoPlayerField,
	},
	{
		id: -193,
		type: FieldType.EndRunButtons,
		defaultValue: '',
		name: 'End run buttons',
		label: 'End run buttons label',
		complexDefaultValue: [],
		settingsTitle: 'End run buttons',
		skeletonType: 'image',
		description: 'Add buttons with end run buttons functionality',
		defaultMetadata: {
			options: {
				button_items: null,
				alignment_style: 'center',
			},
		},
		// Components:
		component: EndRunButtonsFieldSettings,
		widgetFieldComponent: EndRunButtonsField,
	},
	{
		id: -136,
		type: FieldType.WidgetFooter,
		defaultValue: '',
		name: 'Footer',
		label: 'Footer',
		complexDefaultValue: [],
		settingsTitle: 'Interface Footer',
		skeletonType: 'image',
		description: 'Add buttons with end run buttons functionality',
		defaultMetadata: {
			options: {
				button_items: null,
				alignment_style: 'right',
				primary_button_enabled: true,
				primary_button_value: 'Done',
				secondary_button_enabled: true,
				secondary_button_value: 'Cancel',
			},
		},
		// Components:
		component: FooterFieldSettings,
		widgetFieldComponent: null,
		hidden: true,
	},
];

const FIELD_CONFIGURATIONS_MAP = FIELD_CONFIGURATIONS_ENRICHED.reduce((result, conf) => {
	result[conf.type] = conf;
	return result;
}, {});

export const DEFAULT_SKELETON = 'heading, text';

export default FIELD_CONFIGURATIONS_MAP;
